<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
  <v-expand-transition>
    <div id="tasklist" v-if="customerId" :class="tasklistStyle">
      <form>
        <!-- set menu-props="{ maxHeight: '80%', overflowX: true } to make the list scrollable on large screens https://github.com/vuetifyjs/vuetify/issues/2660, makes the width of the list a bit larger but cant find fix. -->
        <v-combobox class="mr-1" ref="taskselect" v-model="selection" solo dense filled :items="items" label="" item-value="id" loading
          prepend-inner-icon="mdi-menu" @click:prepend-inner="menuIconClick()" @update:search-input="searchingForTask"
          item-text="name" return-object multiple full-width :filter="searchFilter"
          :menu-props="{ maxHeight: '80%', overflowX: true }">
          <template v-slot:progress>
            <v-progress-linear
              :active="loading.getTasks || loading.updateTasks || loading.getProjects || loading.changeStatus"
              indeterminate absolute top height="8" color="orange">
            </v-progress-linear>
          </template>

          <template v-slot:selection="data">
            <v-chip v-if="data.item.isStatus" close @click:close="remove(data.item)" label>
              {{ language.t("Tasklist." + data.item.key) }}
            </v-chip>
            <v-chip v-else close @click:close="remove(data.item)" label>
              {{ data.item.name }}
            </v-chip>
          </template>

          <template v-slot:item="data">
            <template v-if="(typeof data.item !== 'object')">
              <v-list-item-content v-text="data.item"></v-list-item-content>
            </template>

            <template v-else-if="(data.item.isHeader)">
              <v-list-item-subtitle>{{ data.item.name }}</v-list-item-subtitle>
              <v-list-item-action v-if="data.item.projectHeader">
                <v-icon>mdi-chevron-{{ hideProjects ? 'down' : 'up' }}</v-icon>
              </v-list-item-action>

              <v-list-item-action v-if="data.item.taskHeader">
                <v-icon>mdi-chevron-{{ hideTasks ? 'down' : 'up' }}</v-icon>
              </v-list-item-action>

              <v-list-item-action v-if="data.item.surveyHeader">
                <v-icon>mdi-chevron-{{ hideSurveys ? 'down' : 'up' }}</v-icon>
              </v-list-item-action>
            </template>


            <template v-else-if="data.item.isProject">
              <v-list-item-avatar>
                <v-icon :color="data.item.color">
                  {{ data.item.icon }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content class="v-list-item-bugfix">
                <v-list-item-title>
                  <v-tooltip bottom open-delay="200">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        {{ data.item.name }}
                      </span>
                    </template>
                    <span>{{ data.item.name }}</span>
                  </v-tooltip>
                </v-list-item-title>
              </v-list-item-content>
            </template>

            <template v-else>
              <v-list-item-avatar @click="openTask(data.item)">
                <v-icon v-if="data.item.icon && data.item.color" :color="data.item.color">
                  {{ data.item.icon }}
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content @click="openTask(data.item)" class="v-list-item-bugfix">
                <v-list-item-title>
                  <v-tooltip v-if="data.item.checked" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-if="data.item.checked" small v-bind="attrs" v-on="on">mdi-sync</v-icon>
                    </template>
                    <span>{{ language.t("Tasklist.fileCheck") }}</span>
                  </v-tooltip>

                  <v-tooltip v-if="data.item.taskLarge" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-if="data.item.taskLarge" small v-bind="attrs" v-on="on" color="red">mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ language.t("Tasklist.taskTooLarge") }}</span>
                  </v-tooltip>
                  <v-tooltip bottom open-delay="200">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        {{ data.item.name }}
                      </span>
                    </template>
                    <span>{{ data.item.name }}</span>
                  </v-tooltip>
                </v-list-item-title>
                <v-list-item-subtitle v-if="data.item.date">{{
                  data.item.date
                }}</v-list-item-subtitle>
              </v-list-item-content>

              <v-hover v-slot="{ hover }">
                <v-list-item-action>
                  <v-container class="pa-0" v-if="data.item.status === '__TASKSTATUSTYPE_NEW'">
                    <v-row>
                      <v-col class="pa-0" v-if="data.item.survey">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon small :loading="data.item.id == loadingId"
                              @click="createTask(data.item)">
                              <v-icon :color="hover ? 'green' : 'grey lighten-3'">mdi-plus</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ language.t("Tasklist.convertTask") }}</span>
                        </v-tooltip>
                      </v-col>
                      <v-col class="pa-0" v-else>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon small :loading="data.item.id == loadingId" @click="changeStatus(data.item, '__TASKSTATUSTYPE_DONE')
                              ">
                              <v-icon :color="hover ? 'green' : 'grey lighten-3'">mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ language.t("Tasklist.completeTask") }}</span>
                        </v-tooltip>
                      </v-col>
                      <v-col class="pa-0">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon small :loading="data.item.id == loadingId"
                              @click="changeStatus(data.item, '__TASKSTATUSTYPE_REMOVED')">
                              <v-icon :color="hover ? 'red' : 'grey lighten-3'">mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span v-if="data.item.survey">{{ language.t("Tasklist.removeSurvey") }}</span>
                          <span v-else>{{ language.t("Tasklist.RemoveTask") }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-container>
                  <v-container class="pa-0"
                    v-else-if="data.item.status === '__TASKSTATUSTYPE_DONE' || data.item.status === '__TASKSTATUSTYPE_REMOVED'">
                    <v-row>
                      <v-col class="pa-0">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon small :loading="data.item.id === loadingId"
                              @click="changeStatus(data.item, '__TASKSTATUSTYPE_NEW')">
                              <v-icon :color="hover ? 'green' : 'grey lighten-3'">mdi-refresh
                              </v-icon>
                            </v-btn>
                          </template>
                          <span v-if="data.item.survey">{{ language.t("Tasklist.restoreSurvey") }}</span>
                          <span v-else>{{ language.t("Tasklist.restoreTask") }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-list-item-action>
              </v-hover>
            </template>
          </template>
        </v-combobox>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" @click="addTask()" dark fab small color="green darken-2">
              <v-icon dark>
                mdi-plus
              </v-icon>
            </v-btn>
          </template>
          <span>{{ language.t("Tasklist.createTask") }}</span>
        </v-tooltip>
      </form>
      <TaskCreateSurvey v-if="createTaskFromSurvey" :task="this.taskCreateSurveyItem" v-on:close="closeTaskFromSurvey"
        v-on:update="getTasks()"></TaskCreateSurvey>
    </div>
  </v-expand-transition>
</template>

<script>
import moment from "moment";
import Task from "../models/Task";
import TaskCreateSurvey from "./robotplotter/TaskCreateSurvey";
import * as CONST from "../Constants.js";

export default {
  components: {
    TaskCreateSurvey,
  },
  data() {
    return {
      hideProjects: true,
      hideTasks: false,
      hideSurveys: false,
      requestCancelToken: null,
      loading: {
        getTasks: false,
        updateTasks: false,
        getProjects: false,
        changeStatus: false,
      },
      loadingId: "",
      maxFeaturesAmount: 100000,
      taskOpen: false,

      autoUpdate: true,
      selection: [{
        id: CONST.__TASKSTATUSTYPE_NEW,
        name: this.language.t("Tasklist.taskStatusNew"),
        key: 'taskStatusNew',
        icon: "mdi-square-rounded",
        color: "green",
        isStatus: true,
      }
      ],
      items: [],
      tasks: null,
      projects: [],

      rprOps: [CONST.__OP_MILLINGDEPTHCONTROL],
      rbpOps: [CONST.__OP_MOTIONPLAN, CONST.__OP_SPRAYPOINTS, CONST.__OP_SPRAYSTEERINGLINES],
      searching: false,
      createTaskFromSurvey: false,
      taskCreateSurveyItem: null,
    };
  },
  mounted: function () {
    if (this.$auth.customerId) {
      this.$nextTick(() => {
        this.getTasks(true, true);
        this.getProjects();
      })
    }
  },
  computed: {
    customerId() {
      return this.$auth.customerId;
    },
    tasklistStyle: function () {
      return {
        floating: !this.$vuetify.breakpoint.xsOnly,
        inline: this.$vuetify.breakpoint.xsOnly,
      };
    },
  },
  watch: {
    customerId(val) {
      if (Number.isInteger(val)) {
        this.getProjects();
        this.getTasks(true, true);
      }
    },
    selection(val, oldval) {
      //when removing item selection is changed, this way it doesnt update twice.
      var skipUpdate = false;
      val.forEach((item) => {
        if (item.task) {
          this.remove(item);
          skipUpdate = true;
        } else if (item.isHeader) {
          this.toggleHeader(item);
          this.remove(item);
          skipUpdate = true;
        }
      });
      if (!skipUpdate) this.updateTasks();
    },
    tasks(val) {
      this.updateTasks();
      this.$emit("tasksFromTasklist", val);
    },
    projects() {
      this.updateTasks();
    },
    "language.locale"() {
      this.updateTasks();
    },
    tasklistStyle() {
      if (this.$refs.taskselect.isMenuActive) {
        this.$refs.taskselect.isMenuActive = false;
        this.showTaskList();
      }
    },
    searching(val) {
      this.updateTasks();
    },
  },
  methods: {
    searchFilter(item, queryText, itemText) {
      if (item.isHeader || item.divider || item.isStatus || item.isProject) return true;
      return itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
    },
    searchingForTask(input) {
      if (input) {
        this.searching = true;
      } else {
        this.searching = false;
      }
    },
    closeTaskFromSurvey() {
      this.createTaskFromSurvey = false;
    },
    createTask(item) {
      this.taskCreateSurveyItem = item;
      this.createTaskFromSurvey = true;
    },
    toggleHeader(item) {
      console.log(this.selection)
      if (item.projectHeader) {
        this.hideProjects = !this.hideProjects;
      }
      if (item.taskHeader) {
        this.hideTasks = !this.hideTasks;
      }
      if (item.surveyHeader) {
        this.hideSurveys = !this.hideSurveys;
      }
    },
    openTask(item) {
      console.log(item);
      if (item.task) {
        console.log("openTask");
        this.taskOpen = true;
        this.$refs.taskselect.blur();

        var taskIndex = this.tasks.findIndex((t) => {
          return t.taskId == item.taskId;
        });

        this.$emit("openTask", item.taskId);
      }
    },
    changeStatus(item, status) {
      this.loading.changeStatus = true;
      this.loadingId = item.id;
      if (item.task) {
        this.$axios.get(route("api.tasks.status", [item.taskId, status])).then((res) => {
          var taskIndex = this.tasks.findIndex((t) => {
            return t.taskId == item.taskId;
          });
          this.tasks[taskIndex].status = status;
          this.updateTasks();
          console.log(res);
          this.loading.changeStatus = false;
          this.loadingId = "";
        });
      }
    },
    closeTask(task) {
      this.taskOpen = false;
      const index = this.selection.findIndex((o) => o.id.startsWith("T") || o.id.startsWith("S"));
      if (index >= 0) this.selection.splice(index, 1);
      // if a task is not null, replace that task with the task in tasks else get all tasks again.
      if (task) {
        var taskIndex = this.tasks.findIndex((t) => {
          return t.taskId == task.taskId;
        });

        this.tasks[taskIndex] = task;
      } else {
        this.getTasks();
      }
      // Show tasklist again
      this.showTaskList();
    },
    updateTasks() {
      console.log("Update tasks");
      if (this.$tasks.length < 1) return;
      this.loading.updateTasks = true;
      var status = /*this.searching ? [] : */[
        {
          id: CONST.__TASKSTATUSTYPE_NEW,
          name: this.language.t("Tasklist.taskStatusNew"),
          key: 'taskStatusNew',
          icon: "mdi-square-rounded",
          color: "green",
          isStatus: true,
        },
        {
          id: CONST.__TASKSTATUSTYPE_DONE,
          name: this.language.t("Tasklist.taskStatusCompleted"),
          key: 'taskStatusCompleted',
          icon: "mdi-square-rounded",
          color: "blue",
          isStatus: true,
        },
        {
          id: CONST.__TASKSTATUSTYPE_REMOVED,
          name: this.language.t("Tasklist.taskStatusRemoved"),
          key: 'taskStatusRemoved',
          icon: "mdi-square-rounded",
          color: "red",
          isStatus: true,
        },
      ];
      /*if (!this.searching)*/ status = [{ name: this.language.t("Tasklist.tasklistHeaderStatus"), isHeader: true }].concat(status, { divider: true });
      var selectionStatuses = this.selection.filter((o) => o.isStatus === true);

      var projects = this.hideProjects ? [] : this.projects.filter((o) => o.active === "1");
      var selectionProjects = this.selection.filter((o) => o.isProject === true);
      /*if (!this.searching)*/  projects = [{ name: this.language.t("Tasklist.tasklistHeaderProjects"), isHeader: true, projectHeader: true }].concat(projects);

      var taskitems = [];
      var surveyitems = [];
      for (let i = 0; i < this.$tasks.length; i++) {
        const task = this.$tasks.all[i]
        var statusFilter = false;
        var projectFilter = false;
        var practiceFilter = false;
        if (selectionStatuses.length > 0) statusFilter = selectionStatuses.some((o) => o.id === task.status);
        else statusFilter = true;
        if (selectionProjects.length > 0) projectFilter = selectionProjects.some((o) => o.projectId === task.project?.projectId);
        else projectFilter = true;

        if (this.$auth.subscription == "__SUB_CIVIL_ROADPROFILER") {
          practiceFilter = this.rprOps.some((o) => o === task.operationPractice);
        } else {
          practiceFilter = this.rbpOps.some((o) => o === task.operationPractice);
        }

        var itemfilter = statusFilter && projectFilter;
        if (!this.hideTasks) {
          if (practiceFilter && (this.selection.length == 0 || itemfilter)) {
            taskitems.push({
              id: "T" + task.taskId,
              task: task,
              status: task.status,
              project: task.project,
              name: task.name,
              taskId: task.taskId,
              checked: task.checked == 0 ? true : false,
              taskLarge: this.getTaskTooLarge(task), //only needed for profiler
              date: moment.utc(task.edited).local().format("YYYY-MM-DD HH:mm"),
              unix: moment.utc(task.edited).format("X"),
              icon: Task.getTaskIcon(task.operationPractice),
              color: Task.getTaskColor(task.status),
            });
          }
        }

        if (this.$auth.subscription != "__SUB_CIVIL_ROADPROFILER") {
          if (!this.hideSurveys && task.operationPractice === CONST.__OP_SURVEY && (this.selection.length == 0 || statusFilter)) {
            surveyitems.push({
              id: "T" + task.taskId,
              task: task,
              status: task.status,
              project: task.project,
              name: task.name,
              taskId: task.taskId,
              checked: task.checked == 0 ? true : false,
              taskLarge: false,
              date: moment.utc(task.edited).local().format("YYYY-MM-DD HH:mm"),
              unix: moment.utc(task.edited).format("X"),
              icon: Task.getTaskIcon(task.operationPractice),
              color: Task.getTaskColor(task.status),
              survey: true,
            });
          }
        }
        taskitems.sort((a, b) => b.unix - a.unix);
      }

      if (this.$auth.subscription != "__SUB_CIVIL_ROADPROFILER") {
        taskitems = [{ divider: true }, { name: this.language.t("Tasklist.tasklistHeaderTasks"), isHeader: true, taskHeader: true }].concat(taskitems);
        surveyitems = [{ divider: true }, { name: this.language.t("Tasklist.tasklistHeaderSurveys"), isHeader: true, surveyHeader: true }].concat(surveyitems);
      } else {
        taskitems = [{ divider: true }, { name: this.language.t("Tasklist.tasklistHeaderTasks"), isHeader: true }].concat(taskitems);
      }

      this.items = status.concat(projects, taskitems, surveyitems);
      this.loading.updateTasks = false;
    },
    getTaskTooLarge(task) {
      var taskTooLarge = false;
      task.getFiles().forEach((file) => {
        if (file.amountFeatures > 100000) taskTooLarge = true;
      });
      return taskTooLarge;
    },
    menuIconClick() {
      this.$emit("menuListClick");
    },
    getTasks(showOnAvailable = false, newFirst = false) {
      console.time("getTasks");
      this.requestCancelToken = this.$axios.CancelToken.source();

      this.loading.getTasks = true;
      var _this = this;
      var filter = newFirst ? "?onlyNew=true" : "";

      this.$axios
        .get(route("api.tasks") + filter, {
          cancelToken: this.requestCancelToken.token,
        })
        .then((res) => {
          // TODO: check status
          this.$tasks.setTasks(res.data.reverse());
          _this.tasks = res.data;
          if (showOnAvailable) _this.showTaskList();
          this.loading.getTasks = false;
          console.timeEnd("getTasks");
          if (newFirst) {
            this.getTasks();
          }

        })
        .catch((err) => {
          console.log(err);
          this.loading.getTasks = false;
        });
    },
    getProjects() {
      this.loading.getProjects = true;
      this.projects = [];
      this.$axios.get(route("api.projects")).then((res) => {
        console.log(res)
        res.data.forEach((project) => {
          this.$projects.addProject(project);
        });
        for (let i = 0; i < res.data.length; i++) {
          this.projects.push({
            id: "P" + res.data[i].projectId,
            projectId: res.data[i].projectId,
            name: res.data[i].name,
            active: res.data[i].active,
            isProject: true,
            icon: "mdi-folder-open",
            color: "green",
          });
        }

        this.loading.getProjects = false;
      });
    },
    showTaskList() {
      if (!this.$refs.taskselect) return;
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.taskselect.isMenuActive = true;
          this.$refs.taskselect.isFocused = true;
        }, 0);
      });
    },
    hideTaskList() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.taskselect.isMenuActive = false;
          this.$refs.taskselect.isFocused = false;
        }, 0);
      });
    },
    remove(item) {
      const index = this.selection.findIndex((o) => o.id === item.id);
      if (index >= 0) this.selection.splice(index, 1);
    },
    addTask() {
      this.$emit("menuSelected", "taskCreate");
    },
  },
};
</script>

<style scoped>
#tasklist {
  z-index: 75;
  margin-left: 5px;
}

#tasklist form {
  display: flex;
  flex: 1 1 auto;
  margin: -12px;
}

.floating {
  position: absolute;
  top: 26px;
  left: 23px;
  width: 412px;
}

.inline {
  position: absolute;
  width: calc(100vw - 23px - 23px);
  top: 18px;
  left: 19px; /* margin of 4 to the left of combobox */
}

/* Manual fix for this bug: https://github.com/vuetifyjs/vuetify/issues/13133 */
/* v-autocomplete using item slot with v-list-item-title and v-list-item-subtitle is rendered too wide */
/* 
.v-autocomplete__content,
.v-list-item-bugfix {
  width: 400px !important;
  max-height: 80% !important;}
  /** this is just to increase the height */
/* */

#tasklist .v-list-item--highlighted,
.v-list-item--highlighted::before {
  opacity: 0 !important; 
}

#tasklist .v-list-item--active,
.v-list-item--active::before {
  opacity: .12 !important;
}

#tasklist .v-input__slot {
  margin-bottom: 0px;
}

#tasklist .v-text-field__details {
  min-height: unset;
  height: 0px;
}

.v-text-field.v-text-field--enclosed .v-text-field__details {
  margin-bottom: 0px;
}
</style>